import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router-dom';
import { settingsBasePath } from 'settings/Settings';
import Loading from 'common/semanticComponents/Loading';
import { AlchemyLoading } from 'common/alchemyComponents/Loading';
import Notifications from 'common/components/Notifications';
import CoachingIntelligenceTnC from 'admin/settings/coaching/CoachingIntelligenceTnC';
import { AlchemyProvider } from '@m/alchemy-ui/AlchemyProviderDocHack';
import useAchievementsEnabled from 'achievements/hooks/useAchievementsEnabled';

const QaReports = lazy(() => import('qa/QaReports'));
const BuilderPage = lazy(() => import('qa/BuilderPage'));
const AlchemyReviewPage = lazy(() => import('qa/alchemyReviewPage'));
const AlchemyAuditPage = lazy(() => import('qa/alchemyAuditPage'));
const AlchemyNewReviewPage = lazy(() => import('qa/alchemyNewReviewPage'));
const CalibrationPage = lazy(() => import('qa/CalibrationPage'));
const AlchemyCalibrationPage = lazy(() => import('qa/alchemyCalibrationPage'));
const CalibrationWizard = lazy(() => import('qa/CalibrationWizard'));
const NewCalibration = lazy(
  () => import('qa/CalibrationSessions/newCalibration/CalibrationWizard')
);
const Calibrations = lazy(() => import('qa/Calibrations'));
const CalibrationSessions = lazy(() => import('qa/CalibrationSessions'));
const AlchemyAssignmentsPage = lazy(() => import('qa/alchemyAssignmentsPage'));
const AlchemyAssignmentDetailsPage = lazy(() => import('qa/alchemyAssignmentDetailsPage'));
const SkippedInteractions = lazy(() => import('qa/alchemyAssignmentSkipsPage'));
const ReviewersPage = lazy(() => import('qa/ReviewersPage'));
const RetryAssignmentPage = lazy(() => import('qa/alchemyAssignmentPage/RetryAssignment'));
const ReviewSimilar = lazy(() => import('qa/alchemyAssignmentPage/ReviewSimilar'));
const AssignmentCompletedPage = lazy(
  () => import('qa/alchemyAssignmentPage/AssignmentCompletedPage')
);
const AssignmentPage = lazy(() => import('qa/AssignmentPage'));
const QaReviewsReader = lazy(() => import('qa/QaReviewsReader'));
const AlchemyCoachingSession = lazy(() => import('alchemyCoachingSession'));
const CoachingTemplateForm = lazy(() => import('coaching_notes/CoachingTemplates/TemplateForm'));
const AlchemyOneOnOnesPage = lazy(() => import('coaching_notes/AlchemyOneOnOnesReporting'));
const EmployeeSurveyPreview = lazy(() => import('admin/EmployeeSurveyPreview'));
const StreamWithReviewRecs = lazy(() => import('stream/StreamWithReviewRecs'));
const SolicitiationPermalink = lazy(() => import('stream/SolicitiationPermalink'));
const StreamAsModal = lazy(() => import('stream/StreamAsModal'));
const FlexWidgetsContainer = lazy(() => import('trends/company/flex/FlexWidgetsContainer'));
const FlexReportsTableContainer = lazy(
  () => import('trends/company/flex/FlexReportsTableContainer')
);
const FlexTagsTableContainer = lazy(() => import('trends/company/flex/FlexTagsTableContainer'));
const AlchemyInteractionsPage = lazy(() => import('qa/alchemyInteractionsPage'));

const PerformancePage = lazy(() => import('alchemyScorecard/PerformancePage'));
const ZendeskFieldMapping = lazy(() => import('applications/integrations/ZendeskFieldMapping'));
const FlexProgramTrends = lazy(() => import('trends/company/flex/FlexProgramTrends'));
const FlexTeamMemberDeliverability = lazy(
  () => import('trends/company/flex/FlexTeamMemberDeliverability')
);
const FlexReportingGroupTrends = lazy(() => import('trends/company/flex/FlexReportingGroupTrends'));
const FlexMultiCompany = lazy(() => import('trends/company/flex/FlexMultiCompany'));
const Settings = lazy(() => import('settings/Settings'));
const MetricConfiguration = lazy(
  () => import('admin/settings/metricsConfiguration/MetricConfiguration')
);
const PromptPreviewer = lazy(() => import('admin/promptPreviewer'));

const Leaderboard = lazy(() => import('leaderboard'));

const NotFound = lazy(() => import('common/components/ErrorPages/NotFound'));
const Unauthorized = lazy(() => import('common/components/ErrorPages/Unauthorized'));

const AchievementsPage = lazy(() => import('achievements'));

/*
Instead of url params, I suggest using query params + utils/locationUtils when possible.
React-router doesn't parse url or query params as nicely as Rails does, so centralizing
our param -> value mapping logic is nice.
  -- Ethan, 2020-11-18
*/

export const AuthorizedRoute = ({ component: Component, isAuthorized, ...rest }) => {
  if (!isAuthorized) {
    return <Redirect to="/403" />;
  }
  return <Route {...rest} render={matchProps => <Component {...matchProps} />} />;
};

const RbacRoutes = ({ permissions, featureFlags }) => {
  const { enabled: achievementsEnabled } = useAchievementsEnabled();

  return (
    <>
      <Notifications />
      <Suspense fallback={<AlchemyLoading fullPage />}>
        <Switch>
          <Route exact path="/403" component={Unauthorized} />
          <AuthorizedRoute
            key="company"
            exact
            path="(/|/admin/company/view)"
            component={StreamWithReviewRecs}
            isAuthorized={permissions.feedbackEnabled}
          />
          <AuthorizedRoute
            exact
            path="/admin/company/one-on-ones/coaching-intelligence-terms-and-conditions"
            component={CoachingIntelligenceTnC}
            isAuthorized={permissions.manageOneOnOneSettings}
          />
          <Route
            key="requests"
            exact
            path="/requests/:uuid"
            render={({ match: { params } }) => <SolicitiationPermalink uuid={params.uuid} />}
          />
          <AuthorizedRoute
            key="trendsTeamMember"
            exact
            path="(/trends/team_member|/agent_trends)"
            component={StreamAsModal}
            isAuthorized={permissions.viewTeamMemberTrendsLink}
          />
          <AuthorizedRoute
            key="trendsProgram"
            exact
            path="/trends/company/program"
            component={FlexProgramTrends}
            isAuthorized={permissions.viewCompanyProgram}
          />
          <AuthorizedRoute
            key="trendsTeamMembers"
            exact
            path="/trends/company/program/team_members"
            component={FlexTeamMemberDeliverability}
            isAuthorized={permissions.viewCompanyProgram}
          />
          <AuthorizedRoute
            key="trendsCompany"
            exact
            path="/trends/company"
            component={FlexWidgetsContainer}
            isAuthorized={permissions.viewCompanyTrends}
          />
          <AuthorizedRoute
            key="trendsTeamOrMembers"
            exact
            path="/trends/company/:resourceName(teams|team_members)"
            component={FlexReportsTableContainer}
            isAuthorized={permissions.viewCompanyTrends}
          />
          <AuthorizedRoute
            key="trendsTags"
            exact
            path="/trends/company/tags"
            component={FlexTagsTableContainer}
            isAuthorized={permissions.viewCompanyTrends}
          />
          <AuthorizedRoute
            key="trendsReportingGroups"
            exact
            path="/trends/company/reporting_groups"
            component={FlexReportingGroupTrends}
            isAuthorized={permissions.viewReportingGroupsTrend}
          />
          <AuthorizedRoute
            key="trendsMulti"
            exact
            path="/trends/company/multi"
            component={FlexMultiCompany}
            isAuthorized={permissions.viewMultiOrganization}
          />
          <AuthorizedRoute
            key="performance"
            exact
            path="/performance"
            component={PerformancePage}
            isAuthorized={permissions.viewPerformance}
          />
          <AuthorizedRoute
            key="oneOnOnes"
            exact
            path="/one_on_ones"
            component={AlchemyOneOnOnesPage}
            isAuthorized={permissions.canViewOneOnOnes}
          />
          <AuthorizedRoute
            key="oneOnOnesSession"
            exact
            path="/one_on_ones/:sessionId"
            component={AlchemyCoachingSession}
            isAuthorized={permissions.canViewOneOnOnes}
          />
          {permissions.manageOneOnOneSettings && [
            <Route
              key="coachingTemplate"
              exact
              path="/one_on_ones/templates/:id"
              render={props => <CoachingTemplateForm {...props} />}
            />,
            <Route
              key="coachingTemplateDuplicate"
              exact
              path="/one_on_ones/templates/:id/duplicate"
              render={props => <CoachingTemplateForm {...props} duplicate />}
            />,
          ]}
          <AuthorizedRoute
            key="qaReport"
            exact
            path="/qa/reports/:reportType"
            component={QaReports}
            isAuthorized={permissions.viewQADashboard}
          />

          <AuthorizedRoute
            key="newReview"
            exact
            path="/qa/reviews/new"
            component={AlchemyNewReviewPage}
            isAuthorized={permissions.reviewQA}
          />
          {(permissions.viewQADashboard || permissions.reviewQA) && [
            <Route
              key="qaReports"
              exact
              path="/qa/reports"
              render={() => <Redirect to="/qa/reports/overview" />}
            />,
            <Route
              key="qaReader"
              exact
              path="/qa/reader"
              render={props => <QaReviewsReader {...props} />}
            />,
            <Route
              key="qaReviews"
              exact
              path="/qa/reviews/:id"
              render={props => <AlchemyReviewPage {...props} />}
            />,
          ]}
          {permissions.manageQASettings && (
            <Route
              key="qaForm"
              exact
              path="/qa/forms/:id"
              render={props => <BuilderPage {...props} />}
            />
          )}
          <AuthorizedRoute
            key="interactions"
            exact
            path="/qa/interactions"
            component={AlchemyInteractionsPage}
            isAuthorized={permissions.reviewQA}
          />
          <AuthorizedRoute
            key="reviewers"
            exact
            path="/qa/reviewers"
            component={ReviewersPage}
            isAuthorized={permissions.viewReviewers}
          />
          {(permissions.manageCalibrations || permissions.reviewQA) && [
            <Route
              key="redirect"
              exact
              path="/qa/calibrations"
              render={() => <Redirect to="/qa/calibrations/overview" />}
            />,
            <Route
              key="overview"
              exact
              path="/qa/calibrations/:tabName(overview|scorecard|all|sessions)"
              render={props => {
                const Component =
                  featureFlags?.calibrations_ui_enhancements ? CalibrationSessions : Calibrations;
                return <Component {...props} />;
              }}
            />,
            <Route
              key="newCalibration"
              exact
              path="/qa/calibrations/new"
              render={props => {
                const Component =
                  featureFlags?.calibrations_ui_enhancements ? NewCalibration : CalibrationWizard;
                return <Component {...props} />;
              }}
            />,
            <Route
              key="calibration"
              exact
              path="/qa/calibrations/:id"
              render={props =>
                (featureFlags?.calibrations_ui_enhancements ?
                  <AlchemyCalibrationPage {...props} />
                : <CalibrationPage {...props} />)
              }
            />,
          ]}
          {permissions.viewAssignments && [
            <Route
              key="assignments"
              exact
              path="/qa/assignments"
              render={props => <AlchemyAssignmentsPage {...props} />}
            />,
            <Route
              key="assignment"
              exact
              path="/qa/assignments/:id"
              render={props => <AssignmentPage {...props} />}
            />,
            <Route
              key="assignmentRetry"
              exact
              path="/qa/assignments/:id/retry"
              render={props => <RetryAssignmentPage {...props} />}
            />,
            <Route
              key="reviewSimilar"
              exact
              path="/qa/assignments/:id/review_similar"
              render={props => <ReviewSimilar {...props} />}
            />,
            <Route
              key="assignmentCompleted"
              exact
              path="/qa/assignments/:id/completed"
              render={props => <AssignmentCompletedPage {...props} />}
            />,
            <Route
              key="assignmentDuplicate"
              exact
              path="/qa/assignments/:id/duplicate"
              render={props => <AssignmentPage {...props} duplicate />}
            />,
            <Route
              key="skippedInteractions"
              exact
              path="/qa/assignments/:id/progress/skipped_interactions"
              render={props => <SkippedInteractions {...props} />}
            />,
            <Route
              key="assignmentDetails"
              exact
              path="/qa/assignments/:id/progress"
              render={props => <AlchemyAssignmentDetailsPage {...props} />}
            />,
            <Route
              key="assignmentEdit"
              exact
              path="/qa/assignments/:id/edit"
              render={props => <AssignmentPage {...props} inEditMode />}
            />,
          ]}
          {(permissions.performQaAudits || permissions.viewQaAudits) && [
            <Route
              key="createAudit"
              exact
              path="/qa/reviews/:reviewId/audit"
              render={props => <AlchemyAuditPage {...props} newAudit />}
            />,
            <Route
              key="audit"
              exact
              path="/qa/audit/:auditId"
              render={props => <AlchemyAuditPage {...props} />}
            />,
          ]}
          <Route
            key="surveyPreview"
            exact
            path="/survey_preview/:uuid"
            component={EmployeeSurveyPreview}
          />
          <Route
            key="surveyPreviewEmployee"
            exact
            path="/survey_preview/:uuid/employee/:employeeId"
            component={EmployeeSurveyPreview}
          />
          <Route
            key="zendesk"
            exact
            path="/zendesk_field_mappings"
            component={ZendeskFieldMapping}
          />
          <Route key="settings" path={settingsBasePath}>
            <Settings />
          </Route>
          <AuthorizedRoute
            key="metricConfiguration"
            exact
            path="/metric_configuration"
            component={MetricConfiguration}
            isAuthorized={permissions.manageMetrics}
          />
          <AuthorizedRoute
            key="leaderboard"
            exact
            path="/leaderboard"
            component={Leaderboard}
            isAuthorized={permissions.viewLeaderboard}
          />
          {permissions.admin && (
            <AuthorizedRoute
              key="coaching-tests"
              exact
              path="/admin/coaching-tests"
              component={PromptPreviewer}
              isAuthorized={permissions.admin}
            />
          )}
          {achievementsEnabled && (
            <Route key="achievements" exact path="/achievements" component={AchievementsPage} />
          )}
          <Route key="notFound" component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
};
export default function Routes() {
  const permissions = useSelector(state => state.permissions);

  const featureFlags = useSelector(state => state.featureFlags);
  const hasRBACEnabled = featureFlags.role_based_access_control;
  const { enabled: achievementsEnabled } = useAchievementsEnabled();

  if (hasRBACEnabled) {
    return <RbacRoutes permissions={permissions} featureFlags={featureFlags} />;
  }

  return (
    <>
      <Notifications />
      <Suspense fallback={<AlchemyLoading fullPage />}>
        <Switch>
          <Route
            key="company"
            exact
            path="(/|/admin/company/view)"
            render={() => <StreamWithReviewRecs />}
          />
          {(permissions.admin || permissions.companyAdmin) && (
            <Route
              path="/admin/company/one-on-ones/coaching-intelligence-terms-and-conditions"
              render={() => <CoachingIntelligenceTnC />}
            />
          )}
          <Route
            key="requests"
            exact
            path="/requests/:uuid"
            render={({ match: { params } }) => <SolicitiationPermalink uuid={params.uuid} />}
          />
          <Route
            key="trendsTeamMember"
            path="(/trends/team_member|/agent_trends)"
            render={() => <StreamAsModal />}
          />
          <Route
            key="newReview"
            exact
            path="/qa/reviews/new"
            render={props => <AlchemyNewReviewPage {...props} />}
          />
          <Route
            key="qaReviews"
            exact
            path="/qa/reviews/:id"
            render={props => <AlchemyReviewPage {...props} />}
          />
          <Route
            key="createAudit"
            exact
            path="/qa/reviews/:reviewId/audit"
            render={props => <AlchemyAuditPage {...props} newAudit />}
          />
          <Route
            key="audit"
            exact
            path="/qa/audit/:auditId"
            render={props => <AlchemyAuditPage {...props} />}
          />
          {(permissions.admin || permissions.companyAdmin) && (
            <Route
              exact
              key="qaForm"
              path="/qa/forms/:id"
              render={props => <BuilderPage {...props} />}
            />
          )}
          <Route
            key="redirect"
            exact
            path="/qa/calibrations"
            render={() => <Redirect to="/qa/calibrations/overview" />}
          />
          <Route
            key="overview"
            exact
            path="/qa/calibrations/:tabName(overview|scorecard|all|sessions)"
            render={props => {
              const Component =
                featureFlags?.calibrations_ui_enhancements ? CalibrationSessions : Calibrations;
              return <Component {...props} />;
            }}
          />
          <Route
            key="newCalibration"
            exact
            path="/qa/calibrations/new"
            render={props => {
              const Component =
                featureFlags?.calibrations_ui_enhancements ? NewCalibration : CalibrationWizard;
              return <Component {...props} />;
            }}
          />
          <Route
            key="calibration"
            exact
            path="/qa/calibrations/:id"
            render={props =>
              (featureFlags?.calibrations_ui_enhancements ?
                <AlchemyCalibrationPage {...props} />
              : <CalibrationPage {...props} />)
            }
          />
          <Route
            key="qaReports"
            exact
            path="/qa/reports"
            render={() => <Redirect to="/qa/reports/overview" />}
          />
          <Route
            key="qaReport"
            exact
            path="/qa/reports/:reportType"
            render={({ match }) => <QaReports reportType={match.params.reportType} />}
          />
          <Route
            key="qaReader"
            exact
            path="/qa/reader"
            render={props => <QaReviewsReader {...props} />}
          />
          <Route
            key="interactions"
            exact
            path="/qa/interactions"
            render={props => <AlchemyInteractionsPage {...props} />}
          />
          <Route
            key="assignments"
            exact
            path="/qa/assignments"
            render={props => <AlchemyAssignmentsPage {...props} />}
          />
          <Route
            key="assignment"
            exact
            path="/qa/assignments/:id"
            render={props => <AssignmentPage {...props} />}
          />
          <Route
            key="assignmentRetry"
            exact
            path="/qa/assignments/:id/retry"
            render={props => <RetryAssignmentPage {...props} />}
          />
          <Route
            key="reviewSimilar"
            exact
            path="/qa/assignments/:id/review_similar"
            render={props => <ReviewSimilar {...props} />}
          />
          <Route
            key="assignmentCompleted"
            exact
            path="/qa/assignments/:id/completed"
            render={props => <AssignmentCompletedPage {...props} />}
          />
          <Route
            key="assignmentDuplicate"
            exact
            path="/qa/assignments/:id/duplicate"
            render={props => <AssignmentPage {...props} duplicate />}
          />
          <Route
            key="skippedInteractions"
            exact
            path="/qa/assignments/:id/progress/skipped_interactions"
            render={props => <SkippedInteractions {...props} />}
          />
          <Route
            key="assignmentDetails"
            exact
            path="/qa/assignments/:id/progress"
            render={props => <AlchemyAssignmentDetailsPage {...props} />}
          />
          <Route
            key="assignmentEdit"
            exact
            path="/qa/assignments/:id/edit"
            render={props => <AssignmentPage {...props} inEditMode />}
          />
          {permissions.viewReviewers && (
            <Route
              key="reviewers"
              exact
              path="/qa/reviewers"
              render={props => <ReviewersPage {...props} />}
            />
          )}
          <Route key="oneOnOnes" exact path="/one_on_ones" component={AlchemyOneOnOnesPage} />
          <Route
            key="oneOnOnesSession"
            exact
            path="/one_on_ones/:sessionId"
            component={AlchemyCoachingSession}
          />
          {permissions.manageOneOnOneSettings && [
            <Route
              key="coachingTemplate"
              exact
              path="/one_on_ones/templates/:id"
              render={props => <CoachingTemplateForm {...props} />}
            />,
            <Route
              key="coachingTemplateDuplicate"
              exact
              path="/one_on_ones/templates/:id/duplicate"
              render={props => <CoachingTemplateForm {...props} duplicate />}
            />,
          ]}
          <Route key="performance" exact path="/performance">
            <PerformancePage />
          </Route>
          {permissions.viewCompanyProgramDashboard && [
            <Route
              key="company-program-trends"
              exact
              path="/trends/company/program"
              component={FlexProgramTrends}
            />,
            <Route
              key="company-program-team-member-deliverability"
              exact
              path="/trends/company/program/team_members"
              component={FlexTeamMemberDeliverability}
            />,
          ]}
          {permissions.viewCompanyTrends && [
            <Route
              key="company-trends"
              exact
              path="/trends/company"
              component={FlexWidgetsContainer}
            />,
            <Route
              key="company-trends-reports"
              exact
              path="/trends/company/:resourceName(teams|team_members)"
              component={FlexReportsTableContainer}
            />,
            <Route
              key="company-trends-tags-report"
              exact
              path="/trends/company/tags"
              component={FlexTagsTableContainer}
            />,
            <Route
              key="reporting-group-trends"
              exact
              path="/trends/company/reporting_groups"
              component={FlexReportingGroupTrends}
            />,
            <Route
              key="linked-account-trends"
              exact
              path="/trends/company/multi"
              component={FlexMultiCompany}
            />,
          ]}
          <Route
            key="surveyPreview"
            exact
            path="/survey_preview/:uuid"
            component={EmployeeSurveyPreview}
          />
          <Route
            key="surveyPreviewEmployee"
            exact
            path="/survey_preview/:uuid/employee/:employeeId"
            component={EmployeeSurveyPreview}
          />
          <Route
            key="zendesk"
            exact
            path="/zendesk_field_mappings"
            component={ZendeskFieldMapping}
          />
          <Route key="settings" path={settingsBasePath}>
            <Settings />
          </Route>
          <AuthorizedRoute
            key="metricConfiguration"
            exact
            path="/metric_configuration"
            component={MetricConfiguration}
            isAuthorized={permissions.manageMetrics}
          />
          <Route key="leaderboard" exact path="/leaderboard" component={Leaderboard} />
          {permissions.admin && (
            <AuthorizedRoute
              key="coaching-tests"
              exact
              path="/admin/coaching-tests"
              component={PromptPreviewer}
              isAuthorized={permissions.admin}
            />
          )}
          {achievementsEnabled && (
            <Route key="achievements" exact path="/achievements" component={AchievementsPage} />
          )}
          <Route key="NotFound" component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}
